import gsap from 'gsap'
import PropTypes from 'prop-types'
import React, {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

/**
 * The `RouteTransition` manages a straightforward route
 * transition behavior. It can be used with routes that
 * don't require more complex transitioning behaviors, such
 * as those with sub-paths like the property item views.
 *
 * @param {object} props - the config props
 * @returns {React.ReactElement} the react element
 */
function RouteTransition (props) {
  const {
    Component,
    data,
    delayHide,
    delayShow,
    durationHide,
    durationShow,
    isActive,
    isBlurred,
    isConditionallyRendered,
    route
  } = props
  const [isRouteActive, setIsRouteActive] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const wasRouteActive = useRef(false)
  const refTransition = useRef({})
  const wasActive = useRef(false)

  /**
   * On hide the route
   */
  const onHide = useCallback(async () => {
    setIsTransitioning(true)

    gsap.killTweensOf(refTransition.current)

    return gsap.to(refTransition.current, {
      duration: durationHide + delayHide
    })
  }, [])

  /**
   * On show the route
   */
  const onShow = useCallback(async () => {
    gsap.killTweensOf(refTransition.current)

    return gsap.to(refTransition.current, {
      duration: durationShow * 0.5 + delayShow
    })
  })

  /**
   * Effect: On mount
   */
  useEffect(() => {
    wasActive.current = false
  }, [])

  /**
   * Effect: When the route state changes
   * (and `isActive`)
   */
  useEffect(() => {
    if (!isActive) {
      wasActive.current = false
      setIsRouteActive(false)

      return
    }

    if (isActive === wasActive.current) {
      // Dismiss
      return
    }

    wasActive.current = isActive

    setIsVisible(true)

    /**
     *
     */
    async function transitionRoutes () {
      setIsRouteActive(true)

      if (wasRouteActive.current) {
        // Has an active route/subroute, hide first
        await onHide()
      }

      await onShow()
      setIsTransitioning(false)
    }

    transitionRoutes()
  }, [isActive, route])

  /**
   * When the `isRouteActive` state changes
   */
  useEffect(() => {
    if (isRouteActive === wasRouteActive.current) {
      // Defensive when no change
      return
    }

    wasRouteActive.current = isRouteActive

    /**
     *
     */
    async function hide () {
      await onHide()
      setIsTransitioning(false)
      setIsVisible(false)
    }

    if (!isRouteActive) {
      hide()
    }
  }, [isRouteActive])

  // Render
  return (
    <>
      {(isVisible || !isConditionallyRendered) && (
        <Suspense fallback={<div />}>
          <Component
            isActive={isRouteActive && !isTransitioning}
            {...data}
            route={route}
            delayHide={delayHide}
            delayShow={delayShow}
            durationHide={durationHide}
            durationShow={durationShow}
            isBlurred={isBlurred}
          />
        </Suspense>
      )}
    </>
  )
}

/** @type {object} */
RouteTransition.propTypes = {
  Component: PropTypes.elementType,
  data: PropTypes.shape(),
  delayHide: PropTypes.number,
  delayShow: PropTypes.number,
  durationHide: PropTypes.number,
  durationShow: PropTypes.number,
  isActive: PropTypes.bool,
  isBlurred: PropTypes.bool,
  isConditionallyRendered: PropTypes.bool,
  route: PropTypes.shape()
}

/** @type {object} */
RouteTransition.defaultProps = {
  Component: null,
  data: {},
  delayHide: 0,
  delayShow: 0,
  durationHide: 0.5,
  durationShow: 1,
  isActive: false,
  isBlurred: false,
  isConditionallyRendered: true,
  route: null
}

// Memoize
export default memo(RouteTransition)
